import { TrackService } from '@aiwyn/shared';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SharedModule } from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { CxpTasksDataStateService } from '../../services/cxp-tasks-data-state.service';
import { CxpDeliverableActionsService } from '../../services/cxp-deliverable-actions.service';
import { CxpClientRequestActionsService } from '../../services/cxp-client-request-actions.service';
import { RefreshCxpTasks } from '../../state/cxp-tasks.actions';
import { CxpTask, CxpTaskFilterSelection, CxpTaskStatus } from '../../models';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import {
  CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL,
  CXP_ROUTE_TOKEN_ESIGN_DETAIL,
  CXP_ROUTE_TOKEN_REQUEST_DETAIL,
  RECIPIENT_ID_QUERY_PARAM,
} from '../../models/routing-constants';
import { TaskListComponent } from './components/task-list/task-list.component';
import { TaskFiltersSidebarComponent } from './components/task-filters-sidebar/task-filters-sidebar.component';
import { TaskStaffSidebarComponent } from './components/task-staff-sidebar/task-staff-sidebar.component';
import { CxpFilterChangeEvent } from '../../models';

@Component({
  selector: 'cxp-tasks-container',
  templateUrl: './tasks-container.component.html',
  styleUrls: ['./tasks-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressBarModule, SharedModule, TaskListComponent, TaskFiltersSidebarComponent, TaskStaffSidebarComponent],
})
export class TasksContainerComponent {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly deliverableActions = inject(CxpDeliverableActionsService);
  private readonly clientRequestActions = inject(CxpClientRequestActionsService);
  private readonly trackService = inject(TrackService);
  readonly dataState = inject(CxpTasksDataStateService);

  constructor() {
    this.dataState.backgroundEffects$().pipe(takeUntilDestroyed()).subscribe();
    // Force a fresh load each time the container is mounted — covers the
    // "deliverable just got marked VIEWED, surface it as completed" case
    // when the user navigates back to the list.
    this.store.dispatch(new RefreshCxpTasks());
  }

  /** Container-owned UI state — which sidebar chips are checked. */
  readonly selectedFilters = signal<CxpTaskFilterSelection>({});
  readonly activeTab = signal<CxpTaskStatus>('open');
  /** Container-owned UI state — current value of the search input. */
  readonly searchQuery = signal<string>('');

  /** True when at least one sidebar chip is checked — drives the Clear All button's visibility. */
  readonly hasSelectedFilters = computed(() =>
    Object.values(this.selectedFilters()).some((group) => (group?.size ?? 0) > 0),
  );

  /** Tasks after sidebar selection + search — composed via the facade helper. */
  readonly sidebarFilteredTasks = computed(() => this.dataState.filterTasks(this.selectedFilters(), this.searchQuery()));

  /**
   * Tasks further filtered by the active tab. The selector returns tasks
   * sorted ascending by due-date (most-urgent first) — that's the right
   * order for the Open tab. For Completed we reverse so the most-recently
   * completed sit at the top and oldest sink to the bottom.
   */
  readonly displayedTasks = computed(() => {
    const filtered = this.sidebarFilteredTasks().filter((task) => task.status === this.activeTab());
    return this.activeTab() === 'completed' ? filtered.reverse() : filtered;
  });

  /**
   * Sidebar filter groups scoped to the active tab — counts react to tab
   * changes only, not to search or other chip selections.
   */
  readonly filterGroups = computed(() => this.dataState.filterGroupsFor(this.activeTab()));

  /** Tab options with counts derived from sidebar-filtered tasks. */
  readonly tabOptions = computed(() => {
    const tasks = this.sidebarFilteredTasks();
    const openCount = tasks.filter((t) => t.status === 'open').length;
    const completedCount = tasks.filter((t) => t.status === 'completed').length;
    return [
      { id: 'open' as CxpTaskStatus, label: 'Open', count: openCount },
      { id: 'completed' as CxpTaskStatus, label: 'Completed', count: completedCount },
    ];
  });

  onFilterChanged(event: CxpFilterChangeEvent): void {
    const current = { ...this.selectedFilters() };
    const groupSet = new Set(current[event.groupKey] ?? []);

    if (event.selected) {
      groupSet.add(event.optionId);
    } else {
      groupSet.delete(event.optionId);
    }

    current[event.groupKey] = groupSet;
    this.selectedFilters.set(current);

    this.trackService.track('CXP Tasks Filter Applied', {
      groupKey: event.groupKey,
      selected: event.selected,
      filteredCount: this.sidebarFilteredTasks().length,
    });
  }

  onClearAllFilters(): void {
    this.selectedFilters.set({});
    this.trackService.track('CXP Tasks Filters Cleared', {
      displayedCount: this.displayedTasks().length,
    });
  }

  onTabChanged(tab: CxpTaskStatus): void {
    this.activeTab.set(tab);
    // Clear chip selections. Search query is intentionally preserved.
    this.selectedFilters.set({});
    this.trackService.track('CXP Tasks Tab Changed', {
      tab,
      displayedCount: this.displayedTasks().length,
    });
  }

  onSearchQueryChanged(query: string): void {
    this.searchQuery.set(query);
  }

  onTaskSelected(task: CxpTask): void {
    this.trackService.track('CXP Task Selected', {
      id: task.id,
      type: task.type,
      sourceStatus: task.sourceStatus,
      actionRequired: task.actionRequired,
      integrationType: task.integration?.type,
    });
    if (task.type === 'esign-client-request-list' && task.esignRecipientId != null) {
      // An eSign row opens the eSign detail page — the request path serves a CRL, which has
      // nothing to show for a bundle shell carrying no items. The detail page, never the viewer:
      // it is where the signer reads what they are signing before pressing Review & Sign. The card
      // names which signature it is for, which travels along so that page can tell two signatures
      // on one bundle apart.
      this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_ESIGN_DETAIL, task.sourceId], {
        queryParams: { [RECIPIENT_ID_QUERY_PARAM]: task.esignRecipientId },
      });
    } else if (task.type === 'esign-client-request-list') {
      this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_ESIGN_DETAIL, task.sourceId]);
    } else if (task.type === 'client-request-list') {
      this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_REQUEST_DETAIL, task.sourceId]);
    } else if (task.type === 'deliverable') {
      this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL, task.sourceId]);
    } else if (task.type === 'external-deliverable' && task.integration) {
      // Mark all files VIEWED, await backend, then redirect in same window.
      this.deliverableActions.markAllAndOpenExternal(task.sourceId, task.integration.url);
    } else if (task.type === 'external-client-request-list' && task.integration) {
      // Redirect to the third-party CRL (e.g. FieldGuide) in the same window.
      this.clientRequestActions.openExternal(task);
    }
  }
}
